/* eslint-disable react/no-unescaped-entities */
// v3.15-989 — Money Manager.
// Private internal finance ledger restricted to Adris Davis only.
// Spreadsheet-style table. NO emails. NO automations. Backend strictly
// 403's any non-Adris caller, so this page also 403-redirects on mount.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Plus, Save, Send, Banknote, Trash2, Link2, DollarSign, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
// v3.15-1137A — defensive boundary so a malformed row can never white-screen
// the whole finance ledger.
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import AssignCommissionDrawer from '../components/AssignCommissionDrawer';  // v3.18-1347 PAYROLL-001

// v3.15-1137A — Safe toast error helper. Backend can return `detail` as a
// FastAPI validation array (objects), a plain string, or undefined. React
// would crash when sonner tried to render a raw object. This coerces every
// shape to a single user-readable string.
function safeErrText(err, fallback = 'Save failed') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    // Pydantic v2 shape — already humanised by the v3.15-1137A backend
    // handler, but we still guard in case a route bypasses it.
    return d.map(v => {
      const loc = Array.isArray(v?.loc) ? v.loc.filter(x => x !== 'body').join('.') : '';
      return [loc, v?.msg || v?.type].filter(Boolean).join(': ');
    }).join('; ') || fallback;
  }
  if (d && typeof d === 'object') {
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  if (err?.message) return err.message;
  return fallback;
}

// v3.15-1061 — Money Manager client picker — BULLETPROOF REWRITE.
//
// Operational finance regression fix per Adris's spec:
//   - typing must show matches IMMEDIATELY (120ms debounce)
//   - partial substring match on full_name / email / phone / app# / client#
//     (all delegated to backend /money-manager/client-lookup)
//   - keyboard ArrowUp/Down/Enter/Escape + mouse click both bind
//     authoritative client_id + client_name + deal_amount
//   - hard rule: clicking/Enter on a result MUST persist client_id,
//     no race with input blur or parent re-render
//   - flat list (no grouping) — keeps the hot path simple
//   - console-logged diagnostic trail per Adris's requirement #9
//
// What changed vs v3.15-1057:
//   - Removed the useEffect that re-synced `query` from `row.client_name`
//     (that was the regression — typing got overwritten by parent prop)
//   - Removed company grouping (added complexity, no operational value here)
//   - Selection writes through a `pendingSelection` ref so the blur
//     timeout cannot race with the click
//   - Diagnostic console.log on every lookup, selection, and save payload
//
// Adris-only access (auth is enforced server-side).
const ClientLookupCell = ({ row, idx, onSelect, onTextChange, onBlur, testIdPrefix }) => {
  // Local query state — initialised from the row's client_name on mount,
  // then OWNED by this component until the parent commits a selection.
  const [query, setQuery]     = useState(row.client_name || '');
  const [open, setOpen]       = useState(false);
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const inflight = useRef(0);
  const selectingRef = useRef(false); // true between mousedown/keydown and the parent re-render

  // v3.15-1061: When the parent's row.id changes (e.g. row was just saved
  // and replaced with the canonical record) OR client_id is set externally,
  // resync the displayed name. Critically: we do NOT track row.client_name
  // here — typing must NOT be overwritten.
  useEffect(() => {
    setQuery(row.client_name || '');
    selectingRef.current = false;

  }, [row.id, row.client_id]);

  // v3.15-1061: live search — 120ms debounce.
  useEffect(() => {
    if (!open) return;
    const q = (query || '').trim();
    if (q.length < 2) { setItems([]); setLoading(false); return; }
    const myReq = ++inflight.current;
    setLoading(true);
    const t = setTimeout(() => {

      console.log(`[MM-1061] lookup q=${JSON.stringify(q)} req#${myReq}`);
      api.get('/money-manager/client-lookup', { params: { q, limit: 12 } })
        .then(({ data }) => {
          if (myReq !== inflight.current) return; // stale response
          const list = data?.items || [];

          console.log(`[MM-1061] lookup q=${JSON.stringify(q)} → ${list.length} results`);
          setItems(list);
          setHighlightedIdx(0);
        })
        .catch((err) => {
          if (myReq !== inflight.current) return;

          console.warn('[MM-1061] lookup failed', err?.response?.status, err?.message);
          setItems([]);
        })
        .finally(() => { if (myReq === inflight.current) setLoading(false); });
    }, 120);
    return () => clearTimeout(t);
  }, [query, open]);

  const isLinked = !!row.client_id;

  // The ONE place that fires when a result is chosen. Sets selectingRef
  // FIRST so the blur timeout doesn't snap-close the dropdown mid-commit.
  const commitSelection = (c) => {
    if (!c) return;
    selectingRef.current = true;

    console.log(`[MM-1061] commitSelection row#${idx} → client_id=${c.id} name=${JSON.stringify(c.full_name)} da=${c.deal_amount}`);
    setOpen(false);
    setQuery(c.full_name || '');
    onSelect(idx, c);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!open) setOpen(true);
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && items.length > 0) {
        e.preventDefault();
        commitSelection(items[highlightedIdx] || items[0]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <Input
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Close dropdown after a brief delay so option clicks register;
            // a click on a result calls commitSelection which sets
            // selectingRef.current=true — in that case do NOT fire the
            // parent onBlur (the row save is already scheduled by selectClient).
            setTimeout(() => {
              setOpen(false);
              if (!selectingRef.current && onBlur) onBlur();
            }, 150);
          }}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // Typing manually breaks the link to an existing client_id so
            // we never silently associate the new free-text with the old
            // record (orphan-row prevention is enforced upstream too).
            onTextChange(idx, e.target.value);
          }}
          className={`h-8 text-xs pr-6 ${isLinked ? 'bg-emerald-50 border-emerald-300' : ''}`}
          placeholder="Type to search Hub clients…"
          autoComplete="off"
          spellCheck={false}
          data-testid={`${testIdPrefix}-client_name-${idx}`}
        />
        {isLinked && <Link2 className="h-3 w-3 text-emerald-600 absolute right-1.5 top-2.5 pointer-events-none" />}
      </div>
      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 min-w-[300px] bg-white border border-slate-200 rounded shadow-lg max-h-64 overflow-y-auto"
          data-testid={`mm-client-lookup-${idx}`}
        >
          {loading && (
            <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> searching…
            </div>
          )}
          {!loading && items.length === 0 && (query || '').trim().length >= 2 && (
            <div className="px-3 py-2 text-[11px] text-slate-400 italic">
              No clients found for &quot;{query}&quot;
            </div>
          )}
          {!loading && items.map((c, i) => {
            const isHighlighted = i === highlightedIdx;
            return (
              <button
                type="button"
                key={c.id}
                onMouseDown={(e) => {
                  // Prevent the input from losing focus on mousedown.
                  // Critically also mark `selectingRef` so the input's
                  // blur-timeout (150ms) ignores the imminent close.
                  e.preventDefault();
                  selectingRef.current = true;
                }}
                onMouseEnter={() => setHighlightedIdx(i)}
                onClick={() => commitSelection(c)}
                className={`block w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-emerald-100' : 'hover:bg-emerald-50'}`}
                data-testid={`mm-client-lookup-option-${c.id}`}
              >
                <div className="font-medium text-slate-800 truncate flex items-center gap-2">
                  <span className="truncate">{c.full_name}</span>
                  {c.company_name && (
                    <span className="text-[9px] uppercase tracking-wide text-slate-400 font-normal whitespace-nowrap">
                      {c.company_name}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 truncate">
                  {[c.email, c.phone, c.app_number, c.deal_stage].filter(Boolean).join(' · ')}
                  {c.deal_amount ? ` · $${Number(c.deal_amount).toLocaleString()}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// v3.15-1119 — suggested values for the Source datalist. Frontend hint only.
// Backend never validates against this list — operators may type any custom
// value and it will persist + appear in the Source filter dropdown.
const SOURCE_SUGGESTIONS = [
  'PTC', 'Solar', 'CF360', 'Tradeline', 'Business Funding',
  'Mortgage', 'Insurance', 'Marketing', 'Other',
];

// v3.15-1119 — expense type options for the Expense Ledger modal.
// v3.15-1215 MM-001 — the first five types are "architecture-driven":
// their amounts are consumed from authoritative compensation records
// (compensation_ledger for the first four, payroll_transactions for
// Payroll). The Expense Ledger modal switches to a record-selector UI
// for these types; manual amount entry is not permitted.
const EXPENSE_TYPES = [
  'Commission', 'Bonus', 'Spiff', 'Chargeback', 'Payroll',
  'Marketing', 'Gift Card', 'Referral Fee',
  'Travel', 'Wire Fee', 'Processing Fee', 'Other',
];

// v3.15-1215 MM-001 — the labels that MUST come from authoritative records.
// Must stay in sync with `ARCH_DRIVEN_EXPENSE_TYPES` on the backend.
const ARCH_DRIVEN_EXPENSE_TYPES = new Set([
  'Commission', 'Bonus', 'Spiff', 'Chargeback', 'Payroll',
]);
const EXPENSE_TYPE_TO_SLUG = {
  Commission: 'commission',
  Bonus:      'bonus',
  Spiff:      'spiff',
  Chargeback: 'chargeback',
  Payroll:    'payroll',
};

const FIELDS = [
  // v3.15-1073: Admin Open/Settled status — FAR-LEFT column. NOT connected
  // to SSOT, payroll, commissions, or funding. Pure ledger disposition.
  { key: 'admin_status',      label: 'Status',            type: 'text',   w: 110, readonly: true },
  { key: 'client_name',       label: 'Client',            type: 'text',   w: 180 },
  // v3.15-1119: Source — datalist-style free-text input. See SOURCE_SUGGESTIONS.
  { key: 'source',            label: 'Source',            type: 'source', w: 140 },
  { key: 'deal_amount',       label: 'Deal Amount',       type: 'number', w: 120 },
  { key: 'ptc_pct',           label: 'PTC %',             type: 'number', w: 80  },
  { key: 'paid_to_ptc',       label: 'Paid to PTC',       type: 'number', w: 120 },
  { key: 'paid_to_partner',   label: 'Paid to Partner',   type: 'number', w: 120 },
  { key: 'wire_fee',          label: 'Wire Fee',          type: 'number', w: 90  },
  { key: 'final_wire_amount', label: 'Final Wire Amount', type: 'number', w: 130, readonly: true },
  // v3.15-1119: Net Profit — read-only, computed server-side from
  // paid_to_ptc − total_expenses. Lives between Final Wire Amount and
  // Wire Ref # exactly per spec Part 4.
  { key: 'net_profit',        label: 'Net Profit',        type: 'number', w: 120, readonly: true },
  { key: 'wire_reference',    label: 'Wire Ref #',        type: 'text',   w: 150 },
  { key: 'date_sent',         label: 'Date Sent',         type: 'date',   w: 130 },
  { key: 'deposit_date',      label: 'Deposit Date',      type: 'date',   w: 130 },
  { key: 'status',            label: 'Workflow',          type: 'text',   w: 100, readonly: true },
  { key: 'notes',             label: 'Notes',             type: 'text',   w: 220 },
];

const calcRow = (row) => {
  // v3.15-997 formula:
  //   Paid to PTC      = Deal Amount × PTC% / 100
  //   Paid to Partner  = Deal Amount − Paid to PTC
  //   Final Wire Amt   = Paid to Partner − Wire Fee
  // Defaults: PTC% = 10, Wire Fee = 30 (only when not explicitly set).
  const da  = parseFloat(row.deal_amount);
  const pct = parseFloat(row.ptc_pct);
  const fee = parseFloat(row.wire_fee);
  const eff_pct = Number.isFinite(pct) ? pct : 10;
  const eff_fee = Number.isFinite(fee) ? fee : 30;
  if (!Number.isFinite(da)) return {};
  const paid_to_ptc      = Number((da * eff_pct / 100).toFixed(2));
  const paid_to_partner  = Number((da - paid_to_ptc).toFixed(2));
  const final_wire_amount= Number((paid_to_partner - eff_fee).toFixed(2));
  return { paid_to_ptc, paid_to_partner, final_wire_amount };
};

const calcFinal = (row) => calcRow(row).final_wire_amount ?? '';

const blankRow = () => ({
  __new: true, client_name: '', source: '', deal_amount: '', ptc_pct: 10,
  paid_to_ptc: '', paid_to_partner: '', wire_fee: 30, final_wire_amount: '',
  net_profit: '',           // v3.15-1119: computed server-side
  wire_reference: '', date_sent: '', deposit_date: '',
  status: 'draft',          // workflow status (draft/sent/deposited)
  admin_status: 'open',     // v3.15-1073: Open / Settled ledger disposition
  notes: '',
  expense_ledger: [],       // v3.15-1119: hidden expense ledger
  total_expenses: 0,        // v3.15-1119: computed
});

// v3.15-1215 MM-001 — Authoritative-Record Selector.
// Opens from inside the Expense Ledger modal when the operator picks an
// architecture-driven expense type (Commission / Bonus / Spiff /
// Chargeback / Payroll). Displays PENDING authoritative records fetched
// from `/money-manager/authoritative/{slug}` with the columns required
// by the MM-001 spec: Employee / Role / Plan / Production / Tier /
// Percentage / Amount / Status. Multi-select via row checkboxes; the
// "Add Selected" button bulk-consumes them into the MM row.
const fmtMoney = (n) => n === null || n === undefined || n === ''
  ? '—'
  : `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => (n === null || n === undefined || n === '') ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 })}%`;

const AuthoritativeSelectorModal = ({ open, onClose, expenseType, entryId, onConsumed }) => {
  const slug   = EXPENSE_TYPE_TO_SLUG[expenseType];
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    api.get(`/money-manager/authoritative/${slug}`)
      .then(({ data }) => { if (!cancelled) setItems(data?.items || []); })
      .catch((e) => { if (!cancelled) toast.error(safeErrText(e, 'Failed to load records')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, slug]);

  if (!open || !slug) return null;

  const toggle = (refId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) next.delete(refId); else next.add(refId);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((r) => r.source_ref_id));
    });
  };

  const totalSelected = items
    .filter((r) => selected.has(r.source_ref_id))
    .reduce((acc, r) => acc + Number(r.calculated_amount || 0), 0);

  const addSelected = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one record');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/money-manager/entries/${entryId}/expenses/from-authoritative`,
        { transaction_type: slug, source_ref_ids: Array.from(selected) },
      );
      const added = data?.counts?.added ?? 0;
      const skipped = data?.counts?.skipped ?? 0;
      toast.success(`Added ${added} · Skipped ${skipped}`);
      onConsumed(data.item);
      onClose();
    } catch (e) {
      toast.error(safeErrText(e, 'Failed to consume records'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50"
      onClick={onClose}
      data-testid="mm-authoritative-selector-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[1024px] max-w-[97vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="mm-authoritative-selector"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
          <div>
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-indigo-600" />
              Select {expenseType} Records
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Consuming pending records from
              {' '}
              <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">
                {slug === 'payroll' ? 'payroll_transactions' : `compensation_ledger (source_type=${slug})`}
              </code>
              . Manual entry is disabled for this type.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="mm-authoritative-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1">
          {loading && (
            <div className="py-10 flex items-center justify-center text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading records…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="py-10 text-center text-slate-400 text-sm italic" data-testid="mm-authoritative-empty">
              No pending {expenseType.toLowerCase()} records available.
              <br/>
              <span className="text-[11px] text-slate-500">
                All records for this type have already been consumed or none exist yet.
              </span>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="mm-authoritative-table">
                <thead>
                  <tr className="bg-slate-100 border-b sticky top-0">
                    <th className="px-2 py-2 w-8 text-center">
                      <input
                        type="checkbox"
                        checked={selected.size === items.length && items.length > 0}
                        onChange={toggleAll}
                        data-testid="mm-authoritative-toggle-all"
                      />
                    </th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Employee</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Role</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Plan</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Production</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Tier</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Applied %</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Amount</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Status</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const isSel = selected.has(r.source_ref_id);
                    return (
                      <tr
                        key={r.source_ref_id}
                        className={`border-b last:border-0 cursor-pointer ${isSel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                        onClick={() => toggle(r.source_ref_id)}
                        data-testid={`mm-authoritative-row-${r.source_ref_id}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggle(r.source_ref_id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`mm-authoritative-checkbox-${r.source_ref_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-slate-800 font-medium">{r.employee_name || '—'}</td>
                        <td className="px-2 py-2 text-slate-600">{r.employee_role || '—'}</td>
                        <td className="px-2 py-2 text-slate-600">
                          {r.compensation_plan || '—'}
                          {r.compensation_plan_version ? (
                            <span className="text-[9px] text-slate-400 ml-1">v{r.compensation_plan_version}</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmtMoney(r.current_production)}</td>
                        <td className="px-2 py-2 text-slate-600 capitalize">{r.current_tier || '—'}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmtPct(r.applied_percentage)}</td>
                        <td className="px-2 py-2 text-right text-slate-900 font-semibold">{fmtMoney(r.calculated_amount)}</td>
                        <td className="px-2 py-2">
                          <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            {r.status || 'pending'}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-slate-500">{r.event_date || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-600" data-testid="mm-authoritative-summary">
            <span className="font-medium">{selected.size}</span> selected
            {selected.size > 0 && (
              <span className="ml-3 text-slate-500">
                · total {fmtMoney(totalSelected)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="mm-authoritative-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={addSelected}
              disabled={submitting || selected.size === 0}
              data-testid="mm-authoritative-add-btn"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Selected</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// v3.15-1119 — Expense Ledger Modal.
// Opens from the 💰 button in the Actions column. Renders:
//   • PTC Revenue (from paid_to_ptc)
//   • Existing expenses table (Type / Amount / Notes / Date Added / Delete)
//   • Add Expense form (Type / Amount / Notes / Save)
// Net Profit is recomputed server-side; we lift the updated row through
// `onChange(row)` so MoneyManagerPage can patch its state immediately and
// the Net Profit column reflects the change without a list refresh.
const ExpenseLedgerModal = ({ open, onClose, row, onChange }) => {
  const [draftType, setDraftType]   = useState('Commission');
  const [draftAmt,  setDraftAmt]    = useState('');
  const [draftNote, setDraftNote]   = useState('');
  const [adding, setAdding]         = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // v3.15-1215 MM-001 — controls the AuthoritativeSelectorModal visibility.
  const [authSelectorOpen, setAuthSelectorOpen] = useState(false);
  // v3.18-1347 PAYROLL-001 — controls the Assign Commission drawer.
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);

  const isArchDriven = ARCH_DRIVEN_EXPENSE_TYPES.has(draftType);

  // v3.15-1228 MM-003 — When an architecture-driven expense type is
  // selected, the manual entry controls are removed entirely and the
  // Authoritative Compensation Selector opens automatically. The
  // operator never types a commission amount, and the selector modal is
  // the only interaction path.
  useEffect(() => {
    if (isArchDriven && open && row?.id) {
      setAuthSelectorOpen(true);
    }
  }, [isArchDriven, draftType, open, row?.id]);

  useEffect(() => {
    // Reset draft form whenever the modal opens for a new row.
    if (open) {
      setDraftType('Commission');
      setDraftAmt('');
      setDraftNote('');
    }
  }, [open, row?.id]);

  if (!open || !row) return null;

  const ledger    = Array.isArray(row.expense_ledger) ? row.expense_ledger : [];
  const ptcRev    = Number(row.paid_to_ptc ?? 0) || 0;
  const totalExp  = Number(row.total_expenses ?? 0) || 0;
  const netProfit = Number(row.net_profit ?? (ptcRev - totalExp)) || 0;

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const addExpense = async () => {
    const amt = parseFloat(draftAmt);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount > 0');
      return;
    }
    if (!row.id) {
      toast.error('Save the row first before adding expenses');
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(`/money-manager/entries/${row.id}/expenses`, {
        expense_type: draftType,
        amount: amt,
        notes: draftNote || null,
      });
      onChange(data.item);
      setDraftAmt('');
      setDraftNote('');
      toast.success('Expense added');
    } catch (e) {
      toast.error(safeErrText(e, 'Failed to add expense'));
    } finally {
      setAdding(false);
    }
  };

  const deleteExpense = async (expId) => {
    if (!window.confirm('Delete this expense?')) return;
    setDeletingId(expId);
    try {
      const { data } = await api.delete(`/money-manager/entries/${row.id}/expenses/${expId}`);
      onChange(data.item);
      toast.success('Expense deleted');
    } catch (e) {
      toast.error(safeErrText(e, 'Failed to delete'));
    } finally {
      setDeletingId(null);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso || ''; }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="expense-ledger-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[720px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="expense-ledger-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              Revenue Breakdown
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {row.client_name || 'Unnamed row'}
              {row.source ? ` • ${row.source}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="expense-ledger-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {/* PTC Revenue */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="rounded border bg-slate-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">PTC Revenue</div>
              <div className="text-lg font-semibold text-slate-800" data-testid="expense-ledger-ptc-revenue">
                {fmt(ptcRev)}
              </div>
            </div>
            <div className="rounded border bg-rose-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-rose-700">Total Expenses</div>
              <div className="text-lg font-semibold text-rose-800" data-testid="expense-ledger-total-expenses">
                {fmt(totalExp)}
              </div>
            </div>
            <div className={`rounded border px-3 py-2 ${netProfit >= 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <div className={`text-[10px] uppercase tracking-wide ${netProfit >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>Net Profit</div>
              <div className={`text-lg font-semibold ${netProfit >= 0 ? 'text-emerald-800' : 'text-amber-800'}`} data-testid="expense-ledger-net-profit">
                {fmt(netProfit)}
              </div>
            </div>
          </div>

          <div className="border-t my-3" />

          {/* v3.18-1347 PAYROLL-001 — Assign Commission to Employee */}
          <div className="mb-4 rounded border border-emerald-200 bg-emerald-50/40 px-3 py-2.5 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5" /> Assign Commission to Employee
              </div>
              <div className="text-[11px] text-emerald-800/80 mt-0.5">
                Writes directly to the compensation ledger with status Pending Approval — the earning appears instantly in that employee's My Money Manager.
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setAssignDrawerOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mm-open-assign-commission-btn"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
            </Button>
          </div>

          {/* Expenses table */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700">Expenses</h3>
            <span className="text-[10px] text-slate-500">{ledger.length} row{ledger.length === 1 ? '' : 's'}</span>
          </div>
          <div className="border rounded overflow-hidden">
            <table className="w-full text-xs" data-testid="expense-ledger-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Expense Type</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Amount</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Notes</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Date Added</th>
                  <th className="text-center px-3 py-2 font-medium text-slate-600 w-12">Delete</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400 italic">No expenses yet — add one below.</td></tr>
                )}
                {ledger.map((e) => (
                  <tr key={e.id} className="border-b last:border-0 hover:bg-slate-50 align-top" data-testid={`expense-row-${e.id}`}>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold">{e.expense_type}</span>
                        {e.source_type && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded"
                            title={`Source: ${e.source_type} · ref ${e.source_ref_id}`}
                            data-testid={`expense-source-badge-${e.id}`}
                          >
                            <Link2 className="h-2.5 w-2.5" /> {e.source_type}
                          </span>
                        )}
                      </div>
                      {/* v3.15-1228 MM-003 — enriched employee identity row */}
                      {e.employee_name && (
                        <div className="mt-1 text-[11px] text-slate-700" data-testid={`expense-employee-${e.id}`}>
                          <span className="font-medium">{e.employee_name}</span>
                          {e.employee_role && <span className="text-slate-500"> · {e.employee_role}</span>}
                        </div>
                      )}
                      {(e.company_name || e.compensation_plan_code) && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {e.company_name && <span>{e.company_name}</span>}
                          {e.company_name && e.compensation_plan_code && <span> · </span>}
                          {e.compensation_plan_code && <span>{e.compensation_plan_code}{e.compensation_plan_version ? ` v${e.compensation_plan_version}` : ''}</span>}
                        </div>
                      )}
                      {(e.compensation_tier || e.applied_percent != null) && (
                        <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {e.compensation_tier && (
                            <span className="inline-block bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-medium" data-testid={`expense-tier-${e.id}`}>
                              {e.compensation_tier}
                            </span>
                          )}
                          {e.applied_percent != null && (
                            <span className="inline-block bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded font-medium" data-testid={`expense-applied-pct-${e.id}`}>
                              {e.applied_percent}%
                            </span>
                          )}
                          {e.production_month && <span className="text-slate-500">· prod {e.production_month}</span>}
                        </div>
                      )}
                      {(e.payment_status || e.ledger_status) && e.source_type && (
                        <div className="text-[10px] mt-0.5">
                          <span className={
                            'inline-block px-1.5 py-0.5 rounded border ' +
                            ((e.payment_status || '').toLowerCase() === 'paid'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : (e.payment_status || '').toLowerCase() === 'voided'
                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : 'bg-slate-50 text-slate-700 border-slate-200')
                          } data-testid={`expense-status-${e.id}`}>
                            {(e.payment_status || e.ledger_status || 'pending').replace('_', ' ')}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-800 font-semibold">{fmt(e.amount)}</td>
                    <td className="px-3 py-2 text-slate-600 text-[11px]">{e.notes || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">{fmtDate(e.created_at)}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => deleteExpense(e.id)}
                        disabled={deletingId === e.id}
                        className="text-rose-500 hover:text-rose-700 disabled:opacity-50"
                        data-testid={`expense-delete-${e.id}`}
                        title="Delete expense"
                      >
                        {deletingId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add expense form */}
          <div className="mt-4 border rounded p-3 bg-slate-50">
            <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Expense
            </h4>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className={isArchDriven ? 'col-span-4' : 'col-span-3'}>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Type</label>
                <select
                  value={draftType}
                  onChange={(e) => setDraftType(e.target.value)}
                  className="w-full h-9 text-xs border rounded px-2 bg-white"
                  data-testid="expense-add-type"
                >
                  {EXPENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {isArchDriven ? (
                // v3.15-1228 MM-003 — architecture-driven types: no
                // manual controls at all. The Authoritative Compensation
                // Selector opens automatically the moment this type is
                // selected. The banner explains what's happening and
                // exposes a "Re-open Selector" button in case the
                // operator dismissed the modal.
                <>
                  <div className="col-span-6">
                    <div className="flex items-start gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] text-indigo-800" data-testid="mm-arch-info-banner">
                      <Link2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>{draftType}</strong> is populated from the
                        authoritative V1 records. The selector opened
                        automatically — no manual amount, employee name,
                        or notes are accepted for this expense type.
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Button
                      onClick={() => {
                        if (!row?.id) { toast.error('Save the row first'); return; }
                        setAuthSelectorOpen(true);
                      }}
                      variant="outline"
                      className="w-full h-9 text-xs"
                      data-testid="mm-select-authoritative-btn"
                    >
                      <Link2 className="h-3.5 w-3.5 mr-1" /> Re-open Selector
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-wide text-slate-500">Amount</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={draftAmt}
                      onChange={(e) => setDraftAmt(e.target.value)}
                      className="h-9 text-xs"
                      data-testid="expense-add-amount"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-5">
                    <label className="text-[10px] uppercase tracking-wide text-slate-500">Notes</label>
                    <Input
                      type="text"
                      value={draftNote}
                      onChange={(e) => setDraftNote(e.target.value)}
                      className="h-9 text-xs"
                      data-testid="expense-add-notes"
                      placeholder="optional"
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      onClick={addExpense}
                      disabled={adding}
                      className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                      data-testid="expense-add-save-btn"
                    >
                      {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* v3.15-1215 MM-001 — Authoritative-record selector, mounted
            inside the ExpenseLedgerModal so it inherits its stacking
            context and the operator flow never leaves this row. */}
        <AuthoritativeSelectorModal
          open={authSelectorOpen}
          onClose={() => setAuthSelectorOpen(false)}
          expenseType={draftType}
          entryId={row?.id}
          onConsumed={onChange}
        />

        {/* v3.18-1347 PAYROLL-001 — Assign Commission drawer, mounted
            inside the ExpenseLedgerModal so the flow never leaves the
            active revenue row. */}
        <AssignCommissionDrawer
          isOpen={assignDrawerOpen}
          onClose={() => setAssignDrawerOpen(false)}
          dealId={row?.client_id || null}
          onAssigned={() => { /* Ledger writes directly — no need to patch row state here. */ }}
        />
      </div>
    </div>
  );
};

export default function MoneyManagerPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  // v3.15-997: monthly tracking — defaults to current calendar month.
  const _now = new Date();
  const [filterMonth, setFilterMonth] = useState(_now.getMonth() + 1);   // 1..12
  const [filterYear,  setFilterYear]  = useState(_now.getFullYear());
  const [dateField,   setDateField]   = useState('date_sent');
  // v3.15-1073: admin_status filter — "all" | "open" | "settled"
  const [adminStatusFilter, setAdminStatusFilter] = useState('all');
  // v3.15-1119: Source filter — "all" or any free-text source value.
  const [sourceFilter, setSourceFilter] = useState('all');
  // v3.15-1119: dynamic list of distinct sources for the filter dropdown.
  const [knownSources, setKnownSources] = useState([]);
  // v3.15-1119: expense ledger modal — which row is being edited.
  const [expenseLedgerRowId, setExpenseLedgerRowId] = useState(null);

  // v3.15-1073 access control: PTC Admin role only (executive finance ledger).
  // Hidden entirely from reps, team members, concierges, marketing partners,
  // managers, directors, and manager directors. Backend enforces the same gate.
  const canUseMoneyManager = typeof isAdmin === 'function' && isAdmin();

  useEffect(() => {
    if (!canUseMoneyManager) {
      navigate('/');
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = { month: filterMonth, year: filterYear, date_field: dateField };
    if (adminStatusFilter !== 'all') params.admin_status = adminStatusFilter;
    if (sourceFilter !== 'all')      params.source       = sourceFilter;
    api.get('/money-manager/entries', { params })
      .then(({ data }) => { if (!cancelled) setRows([...(data.items || []), blankRow()]); })
      .catch(() => { if (!cancelled) toast.error('Access denied or load failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canUseMoneyManager, navigate, filterMonth, filterYear, dateField, adminStatusFilter, sourceFilter]);

  // v3.15-1119: keep the Source filter dropdown's options fresh — pulls
  // distinct values from the backend so new free-text sources show up
  // automatically without redeploys.
  useEffect(() => {
    if (!canUseMoneyManager) return;
    let cancelled = false;
    api.get('/money-manager/sources')
      .then(({ data }) => { if (!cancelled) setKnownSources(data?.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canUseMoneyManager, rows.length]);

  const updateLocal = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      // v3.15-1055: mark row dirty so auto-save-on-blur knows to persist it.
      const merged = { ...r, ...patch, _dirty: true };
      // v3.15-997: recompute the three derived fields whenever a contributing
      // field changes, unless the caller explicitly supplied one of them in
      // the same patch.
      if (['deal_amount', 'ptc_pct', 'wire_fee'].some((k) => k in patch)) {
        const c = calcRow(merged);
        if (c.paid_to_ptc      !== undefined && !('paid_to_ptc'      in patch)) merged.paid_to_ptc      = c.paid_to_ptc;
        if (c.paid_to_partner  !== undefined && !('paid_to_partner'  in patch)) merged.paid_to_partner  = c.paid_to_partner;
        if (c.final_wire_amount!== undefined && !('final_wire_amount'in patch)) merged.final_wire_amount= c.final_wire_amount;
      }
      return merged;
    }));
  };

  // v3.15-1055: AUTO-SAVE ON BLUR. The Save button lives in an Actions column
  // that can scroll off-screen on narrow viewports — operators were editing
  // Wire Ref #, Date Sent, Deposit Date, Notes, etc. and assuming the values
  // persisted. They did not (the row stayed in local-only `_dirty` state).
  // This handler now persists every dirty edit the moment focus leaves the
  // cell, mirroring the spreadsheet UX operators expect.
  //
  // Rules:
  //  - Existing row (has `id`)             → always auto-save on blur
  //  - New row with client_id linked       → auto-save creates the row
  //  - New row with client_name but NO client_id → silent skip
  //       (v3.15-1057: orphan-row prevention — typing free text without
  //        selecting a Hub client must NOT create an unlinked row)
  //  - New row without client_name         → silent skip
  //  - Concurrent save in flight           → skip (single-flight via savingId)
  //  - Not dirty                           → skip
  const autoSaveOnBlur = (idx) => {
    setRows((prev) => {
      const row = prev[idx];
      if (!row) return prev;
      if (!row._dirty) return prev;
      if (savingId !== null) return prev;
      const isNew = row.__new || !row.id;
      if (isNew) {
        // v3.15-1057: require a linked client_id for new-row creation.
        // Free-text-only new rows stay client-side-only.
        if (!row.client_id) return prev;
        if (!String(row.client_name || '').trim()) return prev;
      }
      setTimeout(() => { void saveRow(idx); }, 0);
      return prev;
    });
  };

  // v3.15-1000: link a row to an existing Hub client + auto-populate.
  const selectClient = (idx, c) => {
    const dealAmount = (c.deal_amount === undefined || c.deal_amount === null) ? '' : Number(c.deal_amount);
    updateLocal(idx, {
      client_id:   c.id,
      client_name: c.full_name,
      deal_amount: dealAmount,
      // PTC% / Wire Fee defaults are already 10/30 on a fresh row; respect any
      // existing pinned values otherwise.
    });
    toast.success(`Linked: ${c.full_name}`);
    // v3.15-1055: linking a client should immediately persist (and create the
    // row if new) so the spreadsheet reflects the chosen client without
    // requiring a manual Save click.
    setTimeout(() => autoSaveOnBlur(idx), 50);
  };

  // Manual typing breaks the link so we never silently keep an old client_id.
  const clientNameTextChange = (idx, val) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      // v3.15-1055: mark dirty so auto-save-on-blur fires when focus leaves.
      const merged = { ...r, client_name: val, _dirty: true };
      if (r.client_id) merged.client_id = '';   // unlink when text diverges
      return merged;
    }));
  };

  const saveRow = async (idx) => {
    // v3.15-1057: read the row from FRESH state via setRows-callback to
    // avoid stale closure (the selectClient → autoSaveOnBlur → saveRow
    // chain captures saveRow before the client-link state update flushes).
    let row = null;
    setRows((prev) => { row = prev[idx]; return prev; });
    if (!row) return;
    if (!row.client_name?.trim()) { toast.error('Client name required'); return; }
    // v3.15-1057: orphan-row prevention — new rows MUST be linked to an
    // authoritative Hub client. Free-text manual entries are blocked.
    const isNew = row.__new || !row.id;
    if (isNew && !row.client_id) {
      toast.error('Select a client from the dropdown — free-text entries are not allowed');
      return;
    }
    // v3.15-1061: diagnostic trail per Adris's spec item #9.

    console.log(`[MM-1061] saveRow idx=${idx} client_id=${row.client_id} client_name=${JSON.stringify(row.client_name)} isNew=${isNew} deal=${row.deal_amount}`);
    setSavingId(row.id || `new-${idx}`);
    try {
      const payload = { ...row };
      delete payload.__new;
      delete payload._dirty; // v3.15-1055: never send the client-side dirty flag
      // v3.15-1137A — strip server-computed fields. Sending `net_profit: ''`
      // (the blankRow default) used to white-screen the page because Pydantic
      // returned a 422 array that React couldn't render. `_calc_money_fields`
      // recomputes these on every write, so omitting them is the canonical fix.
      delete payload.net_profit;
      delete payload.total_expenses;
      ['deal_amount', 'ptc_pct', 'paid_to_ptc', 'paid_to_partner', 'wire_fee', 'final_wire_amount']
        .forEach((k) => { payload[k] = payload[k] === '' ? null : Number(payload[k]); });
      let res;
      if (row.__new || !row.id) {
        delete payload.id;
        res = await api.post('/money-manager/entries', payload);
        const created = res.data.item;
        // v3.15-1055: single, clean state update — replace the new row in-place
        // and ensure exactly one trailing blank row exists.
        setRows((prev) => {
          const next = prev.map((r, i) => (i === idx ? { ...created, _dirty: false } : r));
          return next.some((r) => r.__new) ? next : [...next, blankRow()];
        });
      } else {
        res = await api.put(`/money-manager/entries/${row.id}`, payload);
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...res.data.item, _dirty: false } : r)));
      }
      toast.success('Saved', { duration: 1500 });
    } catch (e) {
      toast.error(safeErrText(e, 'Save failed'));
    } finally {
      setSavingId(null);
    }
  };

  const markSent = async (idx) => {
    const row = rows[idx];
    if (!row?.id) { toast.error('Save first'); return; }
    try {
      const { data } = await api.post(`/money-manager/entries/${row.id}/mark-sent`);
      setRows((prev) => prev.map((r, i) => (i === idx ? data.item : r)));
      toast.success('Marked sent');
    } catch (e) { toast.error(safeErrText(e, 'Failed')); }
  };

  const markDeposited = async (idx) => {
    const row = rows[idx];
    if (!row?.id) { toast.error('Save first'); return; }
    try {
      const { data } = await api.post(`/money-manager/entries/${row.id}/mark-deposited`);
      setRows((prev) => prev.map((r, i) => (i === idx ? data.item : r)));
      toast.success('Marked deposited');
    } catch (e) { toast.error(safeErrText(e, 'Failed')); }
  };

  // v3.15-1073: Admin-only Open/Settled disposition. NOT connected to SSOT.
  const toggleAdminStatus = async (idx) => {
    const row = rows[idx];
    if (!row?.id) { toast.error('Save first'); return; }
    const target = (row.admin_status === 'settled') ? 'open' : 'settled';
    const verb   = target === 'settled' ? 'Settle this row?' : 'Reopen this row?';
    if (!window.confirm(`${verb}\n\nThis is an admin tracking flag only. It does NOT touch payroll, commissions, funding, or the SSOT.`)) return;
    try {
      const endpoint = target === 'settled' ? 'mark-settled' : 'mark-open';
      const { data } = await api.post(`/money-manager/entries/${row.id}/${endpoint}`);
      setRows((prev) => {
        const next = prev.map((r, i) => (i === idx ? data.item : r));
        // If filter is active and the new admin_status no longer matches, drop the row.
        if (adminStatusFilter !== 'all' && data.item.admin_status !== adminStatusFilter) {
          return next.filter((_, i) => i !== idx);
        }
        return next;
      });
      toast.success(target === 'settled' ? 'Marked settled' : 'Reopened');
    } catch (e) { toast.error(safeErrText(e, 'Failed')); }
  };

  const deleteRow = async (idx) => {
    const row = rows[idx];
    if (row.__new) {
      setRows((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/money-manager/entries/${row.id}`);
      setRows((prev) => prev.filter((_, i) => i !== idx));
      toast.success('Deleted');
    } catch (e) { toast.error(safeErrText(e, 'Failed')); }
  };

  const totals = useMemo(() => {
    const sum = (k) => rows.reduce((acc, r) => acc + (Number.isFinite(parseFloat(r[k])) ? parseFloat(r[k]) : 0), 0);
    // v3.15-1119: split totals by admin_status="settled" for collected
    // figures; keep ALL-rows sums for projected figures.
    const num = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0);
    let projectedRevenue = 0;
    let collectedRevenue = 0;
    let totalExpenses    = 0;
    let settledExpenses  = 0;
    for (const r of rows) {
      const ptc = num(r.paid_to_ptc);
      const exp = num(r.total_expenses);
      projectedRevenue += ptc;
      totalExpenses    += exp;
      if (r.admin_status === 'settled') {
        collectedRevenue += ptc;
        settledExpenses  += exp;
      }
    }
    const projectedNetProfit = projectedRevenue - totalExpenses;
    const collectedNetProfit = collectedRevenue - settledExpenses;
    return {
      deal_amount: sum('deal_amount'),
      paid_to_ptc: sum('paid_to_ptc'),
      paid_to_partner: sum('paid_to_partner'),
      wire_fee: sum('wire_fee'),
      final_wire_amount: sum('final_wire_amount'),
      net_profit: sum('net_profit'),
      // v3.15-1119 totals block
      projectedRevenue,
      collectedRevenue,
      totalExpenses,
      projectedNetProfit,
      collectedNetProfit,
    };
  }, [rows]);

  if (!canUseMoneyManager) return null;

  return (
    <SectionErrorBoundary label="Money Manager" testId="money-manager-error-boundary">
    <div data-testid="money-manager-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Banknote className="h-6 w-6 text-emerald-600" /> Money Manager
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Private internal finance ledger. No emails. No automations.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* v3.15-997: month/year/date_field filter */}
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(parseInt(e.target.value, 10))}
            className="h-9 text-xs border rounded px-2 bg-white"
            data-testid="money-manager-month-select"
          >
            {[
              [1,'Jan'],[2,'Feb'],[3,'Mar'],[4,'Apr'],[5,'May'],[6,'Jun'],
              [7,'Jul'],[8,'Aug'],[9,'Sep'],[10,'Oct'],[11,'Nov'],[12,'Dec'],
            ].map(([n, l]) => (
              <option key={n} value={n}>{l}</option>
            ))}
          </select>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
            className="h-9 text-xs border rounded px-2 bg-white"
            data-testid="money-manager-year-select"
          >
            {Array.from({ length: 6 }, (_, i) => _now.getFullYear() - 3 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            className="h-9 text-xs border rounded px-2 bg-white"
            data-testid="money-manager-date-field-select"
          >
            <option value="date_sent">By Date Sent</option>
            <option value="deposit_date">By Deposit Date</option>
          </select>
          {/* v3.15-1073: Open / Settled tab filter */}
          <div className="flex items-center rounded-md border bg-white p-0.5" data-testid="mm-admin-status-tabs">
            {[
              { v: 'all',      l: 'All' },
              { v: 'open',     l: 'Open' },
              { v: 'settled',  l: 'Settled' },
            ].map(({ v, l }) => (
              <button
                key={v}
                type="button"
                onClick={() => setAdminStatusFilter(v)}
                className={`text-xs px-3 h-8 rounded transition-colors ${
                  adminStatusFilter === v
                    ? (v === 'settled'
                        ? 'bg-emerald-100 text-emerald-800 font-medium'
                        : v === 'open'
                          ? 'bg-amber-100 text-amber-800 font-medium'
                          : 'bg-slate-100 text-slate-800 font-medium')
                    : 'text-slate-500 hover:text-slate-800'
                }`}
                data-testid={`mm-admin-status-tab-${v}`}
              >
                {l}
              </button>
            ))}
          </div>
          {/* v3.15-1119: Source filter — options come from /money-manager/sources */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="h-9 text-xs border rounded px-2 bg-white"
            data-testid="mm-source-filter"
            title="Filter by Source"
          >
            <option value="all">All Sources</option>
            {knownSources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Button
            onClick={() => setRows((prev) => [...prev, blankRow()])}
            variant="outline"
            data-testid="money-manager-add-row-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Row
          </Button>
        </div>
      </div>

      {loading && (
        <div className="py-12 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="text-xs w-full" data-testid="money-manager-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                {FIELDS.map((f) => (
                  <th key={f.key} className="text-left px-2 py-2 font-medium text-slate-600 whitespace-nowrap" style={{ minWidth: f.w }}>
                    {f.label}
                  </th>
                ))}
                <th className="text-left px-2 py-2 font-medium text-slate-600 sticky right-0 bg-slate-50 border-l border-slate-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isSettled = row.admin_status === 'settled';
                return (
                <tr
                  key={row.id || `new-${idx}`}
                  className={`border-b ${isSettled ? 'bg-emerald-50/60 hover:bg-emerald-50' : 'hover:bg-slate-50'} ${row._dirty ? 'bg-amber-50/40' : ''}`}
                  data-testid={`money-manager-row-${row.id || `new-${idx}`}`}
                >
                  {FIELDS.map((f) => (
                    <td key={f.key} className="px-1 py-1 align-top">
                      {f.key === 'client_name' ? (
                        <ClientLookupCell
                          row={row}
                          idx={idx}
                          onSelect={selectClient}
                          onTextChange={clientNameTextChange}
                          onBlur={() => autoSaveOnBlur(idx)}
                          testIdPrefix="mm"
                        />
                      ) : f.key === 'admin_status' ? (
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${
                            isSettled
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                          data-testid={`mm-admin-status-${idx}`}
                        >
                          {isSettled ? '● Settled' : '○ Open'}
                        </span>
                      ) : f.type === 'source' ? (
                        // v3.15-1119: free-text Source with a datalist of
                        // suggested values. Operators may pick a suggestion
                        // OR type any custom value; both persist verbatim.
                        <>
                          <Input
                            type="text"
                            list={`mm-source-options-${idx}`}
                            value={row[f.key] ?? ''}
                            onChange={(e) => updateLocal(idx, { [f.key]: e.target.value })}
                            onBlur={() => autoSaveOnBlur(idx)}
                            className="h-8 text-xs"
                            placeholder="e.g. Solar"
                            autoComplete="off"
                            data-testid={`mm-${f.key}-${idx}`}
                          />
                          <datalist id={`mm-source-options-${idx}`}>
                            {/* Union of curated suggestions + dynamically known sources */}
                            {Array.from(new Set([...SOURCE_SUGGESTIONS, ...knownSources])).map((s) => (
                              <option key={s} value={s} />
                            ))}
                          </datalist>
                        </>
                      ) : (
                        <Input
                          type={f.type}
                          value={row[f.key] ?? ''}
                          readOnly={f.readonly}
                          onChange={(e) => updateLocal(idx, { [f.key]: e.target.value })}
                          onBlur={() => { if (!f.readonly) autoSaveOnBlur(idx); }}
                          className={`h-8 text-xs ${f.readonly ? 'bg-slate-50 text-slate-500' : ''}`}
                          data-testid={`mm-${f.key}-${idx}`}
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-1 py-1 whitespace-nowrap sticky right-0 bg-white border-l border-slate-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                    <div className="flex items-center gap-1">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          savingId === (row.id || `new-${idx}`)
                            ? 'bg-blue-100 text-blue-700'
                            : row._dirty
                              ? 'bg-amber-100 text-amber-800'
                              : (row.id ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')
                        }`}
                        data-testid={`mm-row-state-${idx}`}
                      >
                        {savingId === (row.id || `new-${idx}`)
                          ? 'Saving…'
                          : row._dirty
                            ? 'Unsaved'
                            : (row.id ? 'Saved' : 'New')}
                      </span>
                      <Button
                        size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                        onClick={() => saveRow(idx)}
                        disabled={savingId !== null}
                        data-testid={`mm-save-${idx}`}
                        title="Save"
                      >
                        {savingId === (row.id || `new-${idx}`) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                        onClick={() => markSent(idx)}
                        disabled={!row.id || row.status === 'sent' || row.status === 'deposited'}
                        data-testid={`mm-mark-sent-${idx}`}
                        title="Mark Sent"
                      >
                        <Send className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                        onClick={() => markDeposited(idx)}
                        disabled={!row.id || row.status === 'deposited'}
                        data-testid={`mm-mark-deposited-${idx}`}
                        title="Mark Deposited"
                      >
                        <Banknote className="h-3 w-3" />
                      </Button>
                      {/* v3.15-1119: 💰 Manage Expenses — opens the Expense Ledger modal */}
                      <Button
                        size="sm" variant="outline" className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setExpenseLedgerRowId(row.id)}
                        disabled={!row.id}
                        data-testid={`mm-manage-expenses-${idx}`}
                        title="Manage Expenses"
                      >
                        💰
                      </Button>
                      {/* v3.15-1073: Settle / Reopen toggle */}
                      <Button
                        size="sm" variant="outline"
                        className={`h-7 px-2 text-[11px] ${isSettled ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}
                        onClick={() => toggleAdminStatus(idx)}
                        disabled={!row.id}
                        data-testid={`mm-toggle-admin-status-${idx}`}
                        title={isSettled ? 'Reopen this row' : 'Mark Settled'}
                      >
                        {isSettled ? 'Reopen' : 'Settle'}
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-rose-600"
                        onClick={() => deleteRow(idx)}
                        data-testid={`mm-delete-${idx}`}
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-medium">
                {/* v3.18-1346 P2 — no aggregate on Status/Source columns.
                    Em-dash marker retains column alignment without a blank cell. */}
                <td className="px-2 py-2 text-slate-400 text-center">—</td>
                <td className="px-2 py-2 text-slate-600">Totals</td>
                <td className="px-2 py-2 text-slate-400 text-center">—</td>
                <td className="px-2 py-2" data-testid="mm-total-deal">${totals.deal_amount.toLocaleString()}</td>
                <td className="px-2 py-2 text-slate-400 text-center">—</td>
                <td className="px-2 py-2" data-testid="mm-total-ptc">${totals.paid_to_ptc.toLocaleString()}</td>
                <td className="px-2 py-2" data-testid="mm-total-partner">${totals.paid_to_partner.toLocaleString()}</td>
                <td className="px-2 py-2" data-testid="mm-total-fee">${totals.wire_fee.toLocaleString()}</td>
                <td className="px-2 py-2" data-testid="mm-total-final">${totals.final_wire_amount.toLocaleString()}</td>
                <td className="px-2 py-2" data-testid="mm-total-net-profit">${totals.net_profit.toLocaleString()}</td>
                <td colSpan={5} className="text-slate-400 text-center">—</td>
                <td className="text-slate-400 text-center">—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* v3.15-1119: Second totals block — Projected vs Collected. Lives
          BELOW the existing footer per spec Part 5 (keep current footer
          unchanged, add new block beneath). Leadership-facing summary. */}
      {!loading && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mm-leadership-totals">
          {[
            { k: 'projectedRevenue',   l: 'Projected Revenue',   tone: 'slate'   },
            { k: 'collectedRevenue',   l: 'Collected Revenue',   tone: 'emerald' },
            { k: 'totalExpenses',      l: 'Total Expenses',      tone: 'rose'    },
            { k: 'projectedNetProfit', l: 'Projected Net Profit',tone: 'slate'   },
            { k: 'collectedNetProfit', l: 'Collected Net Profit',tone: 'emerald' },
          ].map(({ k, l, tone }) => {
            const v = Number(totals[k] || 0);
            const bg = tone === 'emerald' ? 'bg-emerald-50 border-emerald-200'
                     : tone === 'rose'    ? 'bg-rose-50 border-rose-200'
                     :                      'bg-slate-50 border-slate-200';
            const text = tone === 'emerald' ? 'text-emerald-800'
                       : tone === 'rose'    ? 'text-rose-800'
                       :                      'text-slate-800';
            const label = tone === 'emerald' ? 'text-emerald-700'
                        : tone === 'rose'    ? 'text-rose-700'
                        :                      'text-slate-500';
            return (
              <div key={k} className={`border rounded p-3 ${bg}`} data-testid={`mm-leadership-${k}`}>
                <div className={`text-[10px] uppercase tracking-wide font-medium ${label}`}>{l}</div>
                <div className={`text-lg font-semibold ${text}`}>
                  ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* v3.15-1119: Expense Ledger Modal */}
      <ExpenseLedgerModal
        open={!!expenseLedgerRowId}
        onClose={() => setExpenseLedgerRowId(null)}
        row={rows.find((r) => r.id === expenseLedgerRowId) || null}
        onChange={(updated) => {
          setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        }}
      />
    </div>
    </SectionErrorBoundary>
  );
}
